import React, { PropsWithChildren, ReactElement, ReactNode } from 'react';

import PropTypes from 'prop-types';

import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
} from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { SORT_DIRECTIONS, TABLE_SIZES } from '../consts';
import getTableTheme from '../helpers/getTableTheme';
import { Themeable, IsSorted } from '../types';

export type TableHeaderProps = StylableComponent<HTMLDivElement> &
  Themeable & {
    isResizingColumn?: boolean;
    isResizingAnyColumn?: boolean;
    isPlaceholder?: boolean;
    hasSubHeaders?: boolean;
    canResize?: boolean;
    handleResize?: (event: unknown) => void;
    canSort?: boolean;
    isSorted?: IsSorted;
    toggleSort?: (event: unknown) => void;
    colSpan?: number;
    size?: number | string;
    renderValue?: () => ReactNode;
  };

const preventTextHighlight = (
  e: React.MouseEvent<HTMLDivElement, MouseEvent>,
) => {
  e.preventDefault();
};

const TableHeader = ({
  tableSize,
  size,
  isPlaceholder,
  hasSubHeaders,
  canResize,
  isResizingColumn,
  isResizingAnyColumn,
  handleResize,
  canSort,
  isSorted,
  toggleSort,
  renderValue,
  children,
  style,
  ...props
}: PropsWithChildren<TableHeaderProps>): ReactElement => {
  const tableSizeTheme = getTableTheme(tableSize);
  const isPrimaryRow = !isPlaceholder && !hasSubHeaders;

  return (
    <th
      css={[
        {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          position: 'relative',
          width: size,
          flex: `${size} 0 auto`,
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
          backgroundColor: theme.tokens.colors.background.primary.value,
          '.tableHeaderCellSortIcon': {
            marginLeft: 'auto',
            minWidth: 20,
          },
          padding: `0 ${tableSizeTheme.cellPadding}px`,
          ...(canResize
            ? {
                '&:hover': {
                  '& > [role="separator"]': {
                    backgroundColor: theme.tokens.colors.border.subtle.value,
                  },
                },
              }
            : {}),
          ...(canSort
            ? {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor:
                    theme.tokens.colors.background.neutral.hover.value,
                  '& > .tableHeaderCellUnsortedIcon': {
                    // display: 'inherit',
                    visibility: 'visible',
                  },
                  '& > [role="separator"]': {
                    backgroundColor: theme.tokens.colors.border.subtle.value,
                  },
                },
              }
            : {}),
          ...(isPrimaryRow
            ? {
                fontWeight: theme.typography.bodySmallBold.fontWeight,
                borderColor: theme.tokens.colors.border.subtle.value,
              }
            : {
                fontWeight: theme.typography.bodySmall.fontWeight,
                borderColor: theme.tokens.colors.border.subtler.value,
                borderRightWidth: 1,
                borderRightStyle: 'solid',
                borderRightColor: theme.tokens.colors.border.subtler.value,
                justifyContent: 'center',
                '&:last-child': {
                  borderRightWidth: 0,
                },
              }),
        },
        style,
      ]}
      onMouseDown={(e) => {
        if (canSort && !isResizingAnyColumn) {
          toggleSort?.(e);
        }
      }}
      {...props}
    >
      {children ||
        (!isPlaceholder && (
          <>
            {renderValue?.()}
            {canSort &&
              (() => {
                switch (isSorted) {
                  case SORT_DIRECTIONS.ASC:
                    return (
                      <ArrowUpIcon
                        size={20}
                        className="tableHeaderCellSortIcon"
                      />
                    );
                  case SORT_DIRECTIONS.DESC:
                    return (
                      <ArrowDownIcon
                        size={20}
                        className="tableHeaderCellSortIcon"
                      />
                    );
                  default:
                    return (
                      <ArrowUpDownIcon
                        className="tableHeaderCellSortIcon tableHeaderCellUnsortedIcon"
                        size={20}
                        style={{
                          visibility: 'hidden',
                        }}
                      />
                    );
                }
              })()}
          </>
        ))}
      {canResize && (
        // Will be fixed in https://jira.autodesk.com/browse/DSGN-1840
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          role={'separator'}
          onMouseDown={(e) => {
            e.stopPropagation();
            preventTextHighlight(e);
            handleResize?.(e);
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleResize}
          css={[
            {
              zIndex: 1,
              width: 3,
              height: '100%',
              position: 'absolute',
              right: 0,
              ...(isResizingColumn
                ? {
                    background:
                      theme.tokens.colors.border.selected.subtle.value,
                  }
                : {}),
              '&:hover': {
                cursor: 'col-resize',
              },
            },
          ]}
        />
      )}
    </th>
  );
};

TableHeader.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPlaceholder: PropTypes.bool,
  hasSubHeaders: PropTypes.bool,
  canResize: PropTypes.bool,
  isResizingColumn: PropTypes.bool,
  isResizingAnyColumn: PropTypes.bool,
  handleResize: PropTypes.func,
  canSort: PropTypes.bool,
  isSorted: PropTypes.oneOf([...Object.values(SORT_DIRECTIONS), false]),
  toggleSort: PropTypes.func,
  renderValue: PropTypes.func,
  tableSize: PropTypes.oneOf(Object.values(TABLE_SIZES)),
  children: PropTypes.any,
  style: stylePropType,
};

export default TableHeader;
